import { useState, useEffect, useRef, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    X, Upload, Search, Image as ImageIcon, Video, Music, Loader2, Check, Play,
    Folder, FolderPlus, ChevronRight, ChevronDown, Pencil, Trash2, FolderInput, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
    getMediasPaginated, uploadMedia, validateMediaType, validateFileSize,
    getFolders, createFolder, renameFolder, deleteFolder, moveMedia, deleteManyMedia,
} from '@/services/api/meta-media';
import type { MediaResponse, MediaFolder } from '@/types/media';
import type { MediaEmUso } from '@/services/api/meta-media';
import { PASTA_RAIZ } from '@/types/media';

interface MediaLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (media: MediaResponse) => void;
    pageId: string;
    mediaType?: 'image' | 'video' | 'audio' | 'all';
}

interface EnvioEmAndamento {
    total: number;
    concluidos: number;
    falhas: number;
}

export default function MediaLibraryModal({
    isOpen,
    onClose,
    onSelect,
    pageId,
    mediaType = 'all'
}: MediaLibraryModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [buscaAtiva, setBuscaAtiva] = useState('');
    const [selectedMedia, setSelectedMedia] = useState<MediaResponse | null>(null);
    const [pastaAtual, setPastaAtual] = useState<number>(PASTA_RAIZ);
    const [expandidas, setExpandidas] = useState<Set<number>>(new Set());
    const [organizando, setOrganizando] = useState(false);
    const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
    const [criandoPasta, setCriandoPasta] = useState(false);
    const [nomeNovaPasta, setNomeNovaPasta] = useState('');
    const [renomeando, setRenomeando] = useState<MediaFolder | null>(null);
    const [envio, setEnvio] = useState<EnvioEmAndamento | null>(null);
    const [destinoAberto, setDestinoAberto] = useState(false);
    // Preenchido quando a exclusão é barrada por uso: guarda a lista para
    // o aviso, e serve de sinal de que o próximo clique é o 'apagar mesmo assim'.
    const [emUso, setEmUso] = useState<MediaEmUso[] | null>(null);

    // A busca vale para a biblioteca INTEIRA, não só para a pasta aberta —
    // quem busca não sabe (ou não lembra) em que pasta a arte está.
    const buscando = buscaAtiva.trim().length > 0;

    // Debounce: sem isso cada tecla dispara um request ao servidor.
    useEffect(() => {
        const t = setTimeout(() => setBuscaAtiva(searchQuery), 350);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const { data: pastas = [] } = useQuery({
        queryKey: ['media-folders'],
        queryFn: getFolders,
        enabled: isOpen,
        staleTime: 1000 * 60 * 5,
    });

    const {
        data,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['medias', mediaType, buscando ? 'busca' : pastaAtual, buscaAtiva],
        queryFn: ({ pageParam }) =>
            getMediasPaginated({
                ...(mediaType !== 'all' ? { media_type: mediaType } : {}),
                ...(buscando ? { busca: buscaAtiva } : { folder_id: pastaAtual }),
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
        enabled: isOpen,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
    });

    const medias = data?.pages.flatMap((p) => p.items) ?? [];

    const filhasDe = useMemo(() => {
        const mapa = new Map<number, MediaFolder[]>();
        pastas.forEach((p) => {
            const lista = mapa.get(p.parent_id) ?? [];
            lista.push(p);
            mapa.set(p.parent_id, lista);
        });
        return mapa;
    }, [pastas]);

    const porId = useMemo(() => new Map(pastas.map((p) => [p.id, p])), [pastas]);

    // Caminho da raiz até a pasta aberta, para o rastro no topo.
    const caminho = useMemo(() => {
        const trilha: MediaFolder[] = [];
        let atual = pastaAtual;
        while (atual !== PASTA_RAIZ) {
            const p = porId.get(atual);
            if (!p) break;
            trilha.unshift(p);
            atual = p.parent_id;
        }
        return trilha;
    }, [pastaAtual, porId]);

    const invalidarTudo = () => {
        queryClient.invalidateQueries({ queryKey: ['medias'] });
        queryClient.invalidateQueries({ queryKey: ['media-folders'] });
    };

    const criarPastaMutation = useMutation({
        mutationFn: (nome: string) => createFolder({ nome, parent_id: pastaAtual }),
        onSuccess: (pasta) => {
            invalidarTudo();
            setCriandoPasta(false);
            setNomeNovaPasta('');
            toast({ title: 'Pasta criada', description: `"${pasta.nome}" está pronta.` });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não deu para criar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const renomearMutation = useMutation({
        mutationFn: ({ id, nome }: { id: number; nome: string }) => renameFolder(id, nome),
        onSuccess: () => {
            invalidarTudo();
            setRenomeando(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não deu para renomear',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const excluirMutation = useMutation({
        mutationFn: (id: number) => deleteFolder(id),
        onSuccess: (r, id) => {
            if (pastaAtual === id) setPastaAtual(porId.get(id)?.parent_id ?? PASTA_RAIZ);
            invalidarTudo();
            toast({
                title: 'Pasta excluída',
                description: r.midias_movidas
                    ? `${r.midias_movidas} mídia(s) foram para a pasta de cima. Nenhuma imagem foi apagada.`
                    : 'Nenhuma imagem foi apagada.',
            });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não deu para excluir',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const moverMutation = useMutation({
        mutationFn: (destino: number) =>
            moveMedia({ media_ids: Array.from(marcadas), folder_id: destino }),
        onSuccess: (r) => {
            invalidarTudo();
            setMarcadas(new Set());
            setOrganizando(false);
            setDestinoAberto(false);
            toast({ title: 'Movido', description: `${r.movidas} mídia(s) organizadas.` });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não deu para mover',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Recebe os ids em vez de ler `marcadas`: quando a exclusão é disparada logo
    // depois de mudar a seleção, o closure da mutation ainda traria a lista
    // antiga, e apagaria um conjunto diferente do que a tela mostra.
    const excluirMidiasMutation = useMutation({
        mutationFn: ({ ids, forcar }: { ids: number[]; forcar: boolean }) =>
            deleteManyMedia(ids, forcar),
        onSuccess: (r) => {
            // Nada foi apagado: a API achou as artes em uso e devolveu onde.
            if (r.bloqueado) {
                setEmUso(r.em_uso ?? []);
                return;
            }
            invalidarTudo();
            setMarcadas(new Set());
            setEmUso(null);
            setOrganizando(false);
            toast({ title: 'Excluído', description: `${r.apagadas} mídia(s) apagadas.` });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não deu para excluir',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const livresNaSelecao = emUso
        ? Array.from(marcadas).filter((id) => !emUso.some((m) => m.id === id)).length
        : marcadas.size;

    // Tira da seleção o que está preso e apaga o resto — sem forcar, então se
    // ainda sobrar algo em uso a API barra de novo em vez de apagar no escuro.
    const excluirSoAsLivres = () => {
        if (!emUso) return;
        const livres = new Set(Array.from(marcadas).filter((id) => !emUso.some((m) => m.id === id)));
        if (livres.size === 0) return;
        setMarcadas(livres);
        setEmUso(null);
        excluirMidiasMutation.mutate({ ids: Array.from(livres), forcar: false });
    };

    const descreverUsos = (usos: MediaEmUso['usos']) => {
        const partes: string[] = [];
        if (usos.nodes) partes.push(`${usos.nodes} node(s) de flow`);
        if (usos.broadcasts) partes.push(`${usos.broadcasts} broadcast(s) por enviar`);
        if (usos.templates) partes.push(`${usos.templates} template(s)`);
        return partes.join(' e ');
    };

    // Upload de vários arquivos: um request por arquivo, em sequência. Uma falha
    // no meio não derruba os outros — o contador diz quantos entraram.
    const enviarArquivos = async (arquivos: File[]) => {
        setEnvio({ total: arquivos.length, concluidos: 0, falhas: 0 });
        let ultima: MediaResponse | null = null;
        let falhas = 0;

        for (let i = 0; i < arquivos.length; i++) {
            try {
                ultima = await uploadMedia({
                    file: arquivos[i],
                    page_id: pageId,
                    folder_id: pastaAtual,
                });
            } catch {
                falhas += 1;
            }
            setEnvio({ total: arquivos.length, concluidos: i + 1, falhas });
        }

        setEnvio(null);
        invalidarTudo();
        if (ultima && arquivos.length === 1) setSelectedMedia(ultima);

        if (falhas === arquivos.length) {
            toast({
                variant: 'destructive',
                title: 'Nenhum arquivo subiu',
                description: 'Verifique o tamanho e o formato e tente de novo.',
            });
        } else {
            toast({
                title: 'Upload concluído',
                description: falhas
                    ? `${arquivos.length - falhas} de ${arquivos.length} enviados — ${falhas} falharam.`
                    : `${arquivos.length} arquivo(s) em ${caminho.at(-1)?.nome ?? 'Todas as mídias'}.`,
            });
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const escolhidos = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (escolhidos.length === 0) return;

        // Sem página vinculada ao funil o upload sempre falha (page_id
        // obrigatório no backend) — avisa antes de tentar.
        if (!pageId) {
            toast({
                variant: 'destructive',
                title: 'Nenhuma página vinculada',
                description: 'Este funil não tem nenhuma página do Facebook vinculada. Adicione uma página ao funil antes de enviar mídia.',
            });
            return;
        }

        const invalidos = escolhidos.filter((f) => !validateMediaType(f, mediaType));
        const pesados = escolhidos.filter((f) => !validateFileSize(f, 25));
        const validos = escolhidos.filter(
            (f) => validateMediaType(f, mediaType) && validateFileSize(f, 25)
        );

        if (invalidos.length) {
            toast({
                variant: 'destructive',
                title: `${invalidos.length} arquivo(s) de tipo inválido`,
                description: `Apenas ${mediaType === 'all' ? 'imagens, vídeos e áudios' : mediaType} são permitidos.`,
            });
        }
        if (pesados.length) {
            toast({
                variant: 'destructive',
                title: `${pesados.length} arquivo(s) acima de 25MB`,
                description: 'Esses foram ignorados.',
            });
        }
        if (validos.length) enviarArquivos(validos);
    };

    const handleSelect = () => {
        if (selectedMedia) {
            onSelect(selectedMedia);
            onClose();
        }
    };

    const alternarMarcada = (id: number) => {
        // O aviso vale para a seleção que o gerou: mexeu na seleção, ele some,
        // senão o "Apagar mesmo assim" apagaria uma lista diferente da exibida.
        setEmUso(null);
        setMarcadas((atual) => {
            const proxima = new Set(atual);
            if (proxima.has(id)) proxima.delete(id);
            else proxima.add(id);
            return proxima;
        });
    };

    const abrirPasta = (id: number) => {
        setPastaAtual(id);
        setSearchQuery('');
        setBuscaAtiva('');
        setSelectedMedia(null);
    };

    useEffect(() => {
        if (!isOpen) {
            setSelectedMedia(null);
            setSearchQuery('');
            setBuscaAtiva('');
            setMarcadas(new Set());
            setOrganizando(false);
            setCriandoPasta(false);
            setRenomeando(null);
            setDestinoAberto(false);
            setEmUso(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const getMediaIcon = (tipo: string) => {
        switch (tipo) {
            case 'image': return <ImageIcon className="w-4 h-4" />;
            case 'video': return <Video className="w-4 h-4" />;
            case 'audio': return <Music className="w-4 h-4" />;
            default: return <ImageIcon className="w-4 h-4" />;
        }
    };

    const MediaPreview = ({ media }: { media: MediaResponse }) => {
        switch (media.media_type) {
            case 'image':
                return (
                    <img
                        // A leve primeiro, sempre: o file_url é o arquivo que vai
                        // para a Meta e pesa ~1,5 MB por arte.
                        src={media.thumbnail_url || media.preview_url || media.file_url}
                        alt={media.original_filename || media.filename}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                );

            case 'video':
                return (
                    <div className="relative w-full h-full bg-gray-900">
                        <video
                            src={media.file_url}
                            poster={media.thumbnail_url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                                <Play className="w-6 h-6 text-gray-900 ml-1" />
                            </div>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Music className="w-10 h-10 text-white" />
                    </div>
                );

            default:
                return (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="w-10 h-10 text-gray-400" />
                    </div>
                );
        }
    };

    const ItemDaArvore = ({ pasta, nivel }: { pasta: MediaFolder; nivel: number }) => {
        const filhas = filhasDe.get(pasta.id) ?? [];
        const aberta = expandidas.has(pasta.id);
        const ativa = pastaAtual === pasta.id;

        return (
            <div>
                <div
                    className={`group flex items-center gap-1 pr-1 rounded-md text-sm cursor-pointer ${ativa ? 'bg-cyan-50 text-cyan-800' : 'hover:bg-gray-100 text-gray-700'}`}
                    style={{ paddingLeft: `${nivel * 12 + 4}px` }}
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandidas((atual) => {
                                const proxima = new Set(atual);
                                if (proxima.has(pasta.id)) proxima.delete(pasta.id);
                                else proxima.add(pasta.id);
                                return proxima;
                            });
                        }}
                        className={`p-0.5 rounded hover:bg-gray-200 ${filhas.length ? '' : 'invisible'}`}
                    >
                        {aberta ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>

                    {renomeando?.id === pasta.id ? (
                        <Input
                            autoFocus
                            defaultValue={pasta.nome}
                            onBlur={(e) => renomearMutation.mutate({ id: pasta.id, nome: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') renomearMutation.mutate({ id: pasta.id, nome: e.currentTarget.value });
                                if (e.key === 'Escape') setRenomeando(null);
                            }}
                            className="h-7 text-sm my-0.5"
                        />
                    ) : (
                        <>
                            <div
                                onClick={() => abrirPasta(pasta.id)}
                                className="flex items-center gap-1.5 flex-1 min-w-0 py-1.5"
                            >
                                <Folder className={`w-4 h-4 shrink-0 ${ativa ? 'text-cyan-600' : 'text-gray-400'}`} />
                                <span className="truncate">{pasta.nome}</span>
                                {pasta.total_midias > 0 && (
                                    <span className="text-[11px] text-gray-400 shrink-0">{pasta.total_midias}</span>
                                )}
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); setRenomeando(pasta); }}
                                title="Renomear"
                                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 text-gray-500"
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (confirm(`Excluir a pasta "${pasta.nome}"?\n\nNenhuma imagem será apagada — o conteúdo sobe para a pasta de cima.`)) {
                                        excluirMutation.mutate(pasta.id);
                                    }
                                }}
                                title="Excluir pasta"
                                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </>
                    )}
                </div>

                {aberta && filhas.map((f) => (
                    <ItemDaArvore key={f.id} pasta={f} nivel={nivel + 1} />
                ))}
            </div>
        );
    };

    const subpastasAqui = filhasDe.get(pastaAtual) ?? [];
    const nomeDaPastaAtual = caminho.at(-1)?.nome ?? 'Todas as mídias';

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Biblioteca de Mídias</h2>
                        <p className="text-sm text-gray-600 mt-0.5">
                            Organize por pasta, envie vários arquivos de uma vez
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-3 border-b border-gray-200 flex gap-3 flex-shrink-0 items-center">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Buscar em todas as pastas..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>

                    <Button
                        variant={organizando ? 'default' : 'outline'}
                        onClick={() => {
                            setOrganizando((v) => !v);
                            setMarcadas(new Set());
                        }}
                        title="Selecionar várias mídias para mover de pasta"
                    >
                        <FolderInput className="w-4 h-4 mr-2" />
                        {organizando ? 'Concluir' : 'Organizar'}
                    </Button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                        accept={
                            mediaType === 'image' ? 'image/*' :
                                mediaType === 'video' ? 'video/*' :
                                    mediaType === 'audio' ? 'audio/*' :
                                        'image/*,video/*,audio/*'
                        }
                        disabled={!!envio}
                    />
                    <Button onClick={() => fileInputRef.current?.click()} disabled={!!envio}>
                        {envio ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {envio.concluidos}/{envio.total}
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload
                            </>
                        )}
                    </Button>
                </div>

                <div className="flex-1 flex min-h-0">
                    <div className="w-56 border-r border-gray-200 overflow-y-auto p-2 flex-shrink-0">
                        <div
                            onClick={() => abrirPasta(PASTA_RAIZ)}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer ${pastaAtual === PASTA_RAIZ ? 'bg-cyan-50 text-cyan-800' : 'hover:bg-gray-100 text-gray-700'}`}
                        >
                            <Folder className={`w-4 h-4 ${pastaAtual === PASTA_RAIZ ? 'text-cyan-600' : 'text-gray-400'}`} />
                            <span>Sem pasta</span>
                        </div>

                        {(filhasDe.get(PASTA_RAIZ) ?? []).map((p) => (
                            <ItemDaArvore key={p.id} pasta={p} nivel={0} />
                        ))}

                        {criandoPasta ? (
                            <Input
                                autoFocus
                                placeholder="Nome da pasta"
                                value={nomeNovaPasta}
                                onChange={(e) => setNomeNovaPasta(e.target.value)}
                                onBlur={() => {
                                    if (nomeNovaPasta.trim()) criarPastaMutation.mutate(nomeNovaPasta);
                                    else setCriandoPasta(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && nomeNovaPasta.trim()) criarPastaMutation.mutate(nomeNovaPasta);
                                    if (e.key === 'Escape') { setCriandoPasta(false); setNomeNovaPasta(''); }
                                }}
                                className="h-8 text-sm mt-1"
                            />
                        ) : (
                            <button
                                onClick={() => setCriandoPasta(true)}
                                className="w-full flex items-center gap-1.5 px-2 py-1.5 mt-1 rounded-md text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            >
                                <FolderPlus className="w-4 h-4" />
                                Nova pasta {pastaAtual !== PASTA_RAIZ && `em ${nomeDaPastaAtual}`}
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-auto p-5 min-w-0">
                        <div className="flex items-center gap-1 text-sm text-gray-500 mb-4 flex-wrap">
                            {buscando ? (
                                <span>Resultados para "<span className="font-medium text-gray-700">{buscaAtiva}</span>" em todas as pastas</span>
                            ) : (
                                <>
                                    <button onClick={() => abrirPasta(PASTA_RAIZ)} className="hover:text-cyan-600">
                                        Biblioteca
                                    </button>
                                    {caminho.map((p) => (
                                        <span key={p.id} className="flex items-center gap-1">
                                            <ChevronRight className="w-3.5 h-3.5" />
                                            <button onClick={() => abrirPasta(p.id)} className="hover:text-cyan-600">
                                                {p.nome}
                                            </button>
                                        </span>
                                    ))}
                                </>
                            )}
                        </div>

                        {!buscando && subpastasAqui.length > 0 && (
                            <div className="grid grid-cols-4 gap-3 mb-5">
                                {subpastasAqui.map((p) => (
                                    <button
                                        key={p.id}
                                        onDoubleClick={() => abrirPasta(p.id)}
                                        onClick={() => abrirPasta(p.id)}
                                        className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-cyan-300 hover:bg-cyan-50/50 text-left"
                                    >
                                        <Folder className="w-5 h-5 text-cyan-600 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{p.nome}</p>
                                            <p className="text-xs text-gray-500">{p.total_midias} item(s)</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {isLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
                            </div>
                        ) : medias.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">
                                    {buscando ? 'Nada encontrado com esse nome' : 'Nenhuma mídia nesta pasta'}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {buscando ? 'A busca é pelo nome do arquivo enviado.' : 'Faça upload ou mova mídias para cá.'}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 gap-4">
                                {medias.map((media) => {
                                    const marcada = marcadas.has(media.id);
                                    const selecionada = selectedMedia?.id === media.id;
                                    return (
                                        <div
                                            key={media.id}
                                            onClick={() => organizando ? alternarMarcada(media.id) : setSelectedMedia(media)}
                                            title={media.original_filename || media.filename}
                                            className={`
                                                relative aspect-square rounded-lg overflow-hidden cursor-pointer
                                                transition-all duration-200 border-2
                                                ${(organizando ? marcada : selecionada)
                                                    ? 'border-cyan-500 ring-2 ring-cyan-100'
                                                    : 'border-transparent hover:border-cyan-200'
                                                }
                                            `}
                                        >
                                            <MediaPreview media={media} />

                                            {(organizando ? marcada : selecionada) && (
                                                <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                                                    <Check className="w-4 h-4 text-white" />
                                                </div>
                                            )}

                                            <div className="absolute bottom-2 left-2">
                                                <div className="px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-xs text-white flex items-center gap-1">
                                                    {getMediaIcon(media.media_type)}
                                                    <span className="capitalize">{media.media_type}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {hasNextPage && (
                            <div className="flex justify-center mt-6">
                                <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                                    {isFetchingNextPage ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Carregando...
                                        </>
                                    ) : (
                                        'Carregar mais'
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {emUso && (
                    <div className="px-6 py-3 border-t border-amber-200 bg-amber-50 flex-shrink-0">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-900 min-w-0">
                                <p className="font-medium">
                                    Nada foi apagado. {emUso.length} das selecionadas ainda {emUso.length === 1 ? 'está em uso' : 'estão em uso'}:
                                </p>
                                <ul className="mt-1 space-y-0.5 max-h-24 overflow-y-auto">
                                    {emUso.map((m) => (
                                        <li key={m.id} className="truncate text-xs">
                                            <span className="font-medium">{m.original_filename || m.filename}</span>
                                            {' — '}{descreverUsos(m.usos)}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-xs mt-1.5">
                                    Apagar vai deixar essas artes quebradas onde estão sendo usadas.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="text-sm text-gray-600 min-w-0 truncate">
                        {organizando ? (
                            marcadas.size > 0
                                ? <span className="font-medium">{marcadas.size} selecionada(s)</span>
                                : 'Clique nas mídias que quer mover'
                        ) : selectedMedia ? (
                            <>Selecionado: <span className="font-medium">{selectedMedia.original_filename || selectedMedia.filename}</span></>
                        ) : null}
                    </div>

                    <div className="flex gap-3 items-center">
                        {organizando ? (
                            <>
                            {emUso ? (
                                <>
                                    {/* Saída segura primeiro: o caso comum é ter
                                        selecionado muitas e só algumas estarem presas. */}
                                    <Button
                                        variant="outline"
                                        onClick={excluirSoAsLivres}
                                        disabled={livresNaSelecao === 0 || excluirMidiasMutation.isPending}
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Excluir só as livres ({livresNaSelecao})
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            if (confirm(`Apagar TAMBÉM as ${emUso.length} em uso?

Os flows e broadcasts que usam essas artes vão ficar com imagem quebrada. Não tem como desfazer.`)) {
                                                excluirMidiasMutation.mutate({ ids: Array.from(marcadas), forcar: true });
                                            }
                                        }}
                                        disabled={excluirMidiasMutation.isPending}
                                        className="border-red-300 text-red-700 hover:bg-red-50"
                                    >
                                        {excluirMidiasMutation.isPending ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Excluindo...</>
                                        ) : (
                                            <>Apagar as {marcadas.size} assim mesmo</>
                                        )}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        if (confirm(`Excluir ${marcadas.size} mídia(s)?

O arquivo sai do servidor e não tem como desfazer. As que estiverem em uso em algum flow são barradas antes.`)) {
                                            excluirMidiasMutation.mutate({ ids: Array.from(marcadas), forcar: false });
                                        }
                                    }}
                                    disabled={marcadas.size === 0 || excluirMidiasMutation.isPending}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                    {excluirMidiasMutation.isPending ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Excluindo...</>
                                    ) : (
                                        <><Trash2 className="w-4 h-4 mr-2" />Excluir</>
                                    )}
                                </Button>
                            )}

                            <div className="relative">
                                <Button
                                    onClick={() => setDestinoAberto((v) => !v)}
                                    disabled={marcadas.size === 0 || moverMutation.isPending}
                                >
                                    {moverMutation.isPending ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Movendo...</>
                                    ) : (
                                        <><FolderInput className="w-4 h-4 mr-2" />Mover para...</>
                                    )}
                                </Button>

                                {destinoAberto && (
                                    <div className="absolute bottom-full right-0 mb-2 w-60 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-10">
                                        <button
                                            onClick={() => moverMutation.mutate(PASTA_RAIZ)}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                        >
                                            <Folder className="w-4 h-4 text-gray-400" />
                                            Sem pasta
                                        </button>
                                        {pastas.map((p) => (
                                            <button
                                                key={p.id}
                                                onClick={() => moverMutation.mutate(p.id)}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                            >
                                                <Folder className="w-4 h-4 text-cyan-600" />
                                                <span className="truncate">{p.nome}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                                <Button onClick={handleSelect} disabled={!selectedMedia}>Selecionar</Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
